import { Link } from 'react-router-dom';
import { LegalDoc, LegalSection, LegalList } from '../components/LegalDoc.js';

const CONTACT = 'hello@gilding.app';

/**
 * Privacy page — a plain-English account of exactly what Gilding collects and
 * who it's shared with, kept accurate to what the app actually does. Public,
 * no wallet required.
 */
export function Privacy() {
  return (
    <LegalDoc
      title="Privacy"
      updated="September 8, 2026"
      intro={
        <>
          Gilding turns a sketch into finished art. We keep the data we handle to the minimum needed
          to run that. This page explains what that is, why, and who it touches.
        </>
      }
    >
      <LegalSection title="What we collect">
        <LegalList
          items={[
            <>
              <strong className="text-ink">Wallet address.</strong> When you sign in, your wallet
              signs a message on Ethereum. We record the resulting public wallet address
              to identify your account. We never see or store your private keys or seed phrase.
            </>,
            <>
              <strong className="text-ink">Your creative input.</strong> The sketches and prompts you
              write, the style and shape you pick, images you upload as a reference, and the images
              Gilding generates for you.
            </>,
            <>
              <strong className="text-ink">Session data.</strong> A signed session token (cookie
              and/or bearer token) that keeps you logged in.
            </>,
            <>
              <strong className="text-ink">Abuse-prevention signals.</strong> To meter free gildings
              and stop abuse, we use a one-way <em>hashed</em> version of your IP address (never
              stored in the clear) and an anonymous random device ID kept in your browser.
            </>,
          ]}
        />
      </LegalSection>

      <LegalSection title="What we don't collect">
        <p>
          No email, name, phone number, password, or postal address. And no payment-card or bank
          details: credit purchases use USDC on Base. We retain public transaction
          identifiers, purchased packs, and credited amounts so receipts can be verified and recovered.
        </p>
      </LegalSection>

      <LegalSection title="Reliability, saved work & trait reports">
        <p>We keep recent generation status and purchase history with your account. Your browser may cache your pod for up to 24 hours and save pending job or payment references so you can resume after a refresh. These records are separated by wallet.</p>
        <p>We collect aggregate counts and timing measurements for sign-in, portrait loading, generation, suggestions, and checkout to identify failures. These measurements do not include prompt text, wallet addresses, or uploaded images. Browser event collection respects Do Not Track. Aggregate metrics are retained for up to 31 days; temporary hashed network rate-limit keys expire after two hours.</p>
        <p>If you submit a character-trait report, we store the selected reference, affected output, token identifier, wallet address, and the details you provide for operator review. Reports are not published to the feed.</p>
      </LegalSection>

      <LegalSection title="How we use it">
        <LegalList
          items={[
            'To generate, save, display, and let you share your gildings.',
            'To keep you signed in and show your credit balance, gallery, and gildings.',
            'To run safety moderation on prompts before any image is made.',
            'To enforce free-gild limits, rate limits, and cost ceilings that keep the service running.',
          ]}
        />
      </LegalSection>

      <LegalSection title="Who we share it with (service providers)">
        <p>
          We don&rsquo;t sell your data. To run the service, some of it passes through third-party
          processors, each only for its function:
        </p>
        <LegalList
          items={[
            <>
              <strong className="text-ink">Cloudflare.</strong> Hosting, image storage, and caching.
            </>,
            <>
              <strong className="text-ink">Anthropic (Claude).</strong> Refining and moderating your
              prompt. Your prompt text is sent to Claude.
            </>,
            <>
              <strong className="text-ink">Image models (FAL / FLUX, OpenAI, xAI).</strong> Creating
              the image. Your refined prompt and any reference image you provide are sent to the
              model that renders it.
            </>,
            <>
              <strong className="text-ink">Alchemy.</strong> Reading public NFT and wallet data (for
              example, checking Whacky Whales ownership, or looking up a whale by number).
            </>,
          ]}
        />
      </LegalSection>

      <LegalSection title="On-chain information is public">
        <p>
          Gilding is built on public blockchains. Your wallet address, NFT holdings, and any payment
          or settlement are inherently public and outside our control. Anyone can view them on the
          chain.
        </p>
      </LegalSection>

      <LegalSection title="Cookies & local storage">
        <p>
          We use a session cookie or token to keep you signed in, and browser storage for small
          functional flags (an anonymous device ID for free-gild metering, and whether you&rsquo;ve
          entered the site). We don&rsquo;t use third-party advertising or tracking cookies.
        </p>
      </LegalSection>

      <LegalSection title="How long we keep it">
        <p>
          Rate-limit and cache entries expire on their own, within hours to days. Your gildings and
          any images you upload persist while your account exists or until you delete them. A free
          gilding made before signing in is held briefly so you can claim it into a wallet.
        </p>
      </LegalSection>

      <LegalSection title="Your choices">
        <LegalList
          items={[
            'Sign out at any time to end your session.',
            'Delete gildings you own from your gallery.',
            'Simply not connecting a wallet keeps you anonymous, beyond the abuse signals above.',
          ]}
        />
      </LegalSection>

      <LegalSection title="Children">
        <p>Gilding is not intended for anyone under 18, and we don&rsquo;t knowingly collect their data.</p>
      </LegalSection>

      <LegalSection title="Changes & contact">
        <p>
          We may update this policy as the product evolves; the date above marks the latest version.
          Questions about your data? Reach us at{' '}
          <a href={`mailto:${CONTACT}`} className="text-accent underline-offset-2 hover:underline">
            {CONTACT}
          </a>
          . See also our{' '}
          <Link to="/terms" className="text-accent underline-offset-2 hover:underline">
            Terms of Use
          </Link>
          .
        </p>
      </LegalSection>
    </LegalDoc>
  );
}
